import { useResource, unwrap } from "./client.js";
import type { Resource, Schemas } from "./client.js";

export type Me = Schemas["Me"];
export type DayPlanView = Schemas["DayPlanView"];
export type PlanPrompt = Schemas["PlanPrompt"];
export type BreakSession = Schemas["BreakSession"];
export type RenderExercise = Schemas["RenderExercise"];
export type MyProgress = Schemas["MyProgress"];
export type WorkstationView = Schemas["WorkstationView"];
export type DseCategoryView = Schemas["DseCategoryView"];
export type BhsResultView = Schemas["BhsResultView"];
export type BhsHistory = Schemas["BhsHistory"];
export type OrgAggregate = Schemas["OrgAggregate"];
export type OrgSummary = Schemas["OrgSummary"];
export type Countable = Schemas["Countable"];
export type Rag = Schemas["Rag"];
export type Feedback = Schemas["Feedback"];
export type PeriodKey = Schemas["PeriodKey"];
export type Preferences = Schemas["Preferences"];
export type UiCopy = Schemas["UiCopy"];
export type AdminSession = Schemas["AdminSession"];

export function useMe(): Resource<Me> {
  return useResource(async (api) => unwrap(await api.GET("/v1/me")));
}

export function usePlan(timezone?: string): Resource<DayPlanView> {
  return useResource(
    async (api) => unwrap(await api.GET("/v1/me/plan", { params: { query: timezone ? { timezone } : {} } })),
    [timezone],
  );
}

export function useProgress(): Resource<MyProgress> {
  return useResource(async (api) => unwrap(await api.GET("/v1/me/progress")));
}

export function useWorkstation(): Resource<WorkstationView | null> {
  return useResource(async (api) => {
    const r = await api.GET("/v1/me/dse");
    if (r.response.status === 404) return null;
    return unwrap(r);
  });
}

export function useBhs(): Resource<BhsHistory> {
  return useResource(async (api) => unwrap(await api.GET("/v1/me/bhs")));
}

export function useOrg(orgId: string): Resource<OrgSummary> {
  return useResource(async (api) => unwrap(await api.GET("/v1/orgs/{orgId}", { params: { path: { orgId } } })), [orgId]);
}

export function useOrgDashboard(orgId: string, period: PeriodKey): Resource<OrgAggregate> {
  return useResource(
    async (api) => unwrap(await api.GET("/v1/orgs/{orgId}/dashboard", { params: { path: { orgId }, query: { period } } })),
    [orgId, period],
  );
}

export function useAdminSession(): Resource<AdminSession | null> {
  return useResource(async (api) => {
    const r = await api.GET("/v1/admin/session");
    if (r.response.status === 401) return null;
    return unwrap(r);
  });
}
